<script lang="ts">
  import { lang as langState } from '../lang-state.svelte.js';
  import type { BuildState, BuildSummary } from '../build-state.svelte.js';
  import type { GameData } from '../data.js';
  import { loadHistory, saveBuildToHistory, loadBuildFromHistory, deleteBuildFromHistory, type SavedBuild } from '../build-history.js';
  import { theme, type Theme } from '../theme-state.svelte.js';
  import { encodeBuild } from '../build-url.js';
  import { exportBuildAsPng, shareToTelegram } from '../export-png.js';
  import { i18next } from '../i18n.js';

  const THEME_ICONS: Record<Theme, string> = { dark: '🌙', light: '☀', oled: '⬛', division: '🟧' };
  const THEME_LABELS: Record<Theme, { en: string; ru: string }> = {
    dark: { en: 'Dark', ru: 'Тёмная' },
    light: { en: 'Light', ru: 'Светлая' },
    oled: { en: 'OLED', ru: 'OLED' },
    division: { en: 'Division', ru: 'Division' },
  };

  interface Props {
    build?: BuildState;
    data?: GameData;
    summary?: BuildSummary;
  }

  let { build, data, summary }: Props = $props();

  let lang = $derived(langState.current);
  let buildsOpen = $state(false);
  let saveName = $state('');
  let savedList = $state<SavedBuild[]>([]);

  $effect(() => {
    try { savedList = loadHistory(); } catch { savedList = []; }
  });

  function refreshList() {
    try { savedList = loadHistory(); } catch { savedList = []; }
  }

  function doSave() {
    if (!build) return;
    const name = saveName.trim() || `Build ${new Date().toLocaleTimeString()}`;
    savedList = saveBuildToHistory(build, name);
    saveName = '';
  }

  function doLoad(s: SavedBuild) {
    if (!build) return;
    loadBuildFromHistory(build, s);
    buildsOpen = false;
  }

  function doDelete(name: string) {
    savedList = deleteBuildFromHistory(name);
  }

  let username = $state('');
  let email = $state('');
  let pw = $state('');
  let pwConfirm = $state('');
  let loggedIn = $state(false);
  let authOpen = $state(false);
  let authMode = $state<'login' | 'register'>('login');
  let bugOpen = $state(false);
  let bugTitle = $state('');
  let bugBody = $state('');
  let loginMsg = $state('');
  let busy = $state(false);

  $effect(() => {
    try {
      const raw = localStorage.getItem('divcalc:auth');
      if (raw) {
        const a = JSON.parse(raw);
        if (a?.token && a?.username) { username = a.username; loggedIn = true; }
      }
    } catch { /* ignore */ }
  });

  function openAuth(mode: 'login' | 'register') {
    authMode = mode;
    authOpen = true;
    bugOpen = false;
    loginMsg = '';
  }

  async function doLogin() {
    if (!username || !pw) {
      loginMsg = lang === 'ru' ? 'Введи логин/email и пароль' : 'Enter username/email and password';
      return;
    }
    busy = true;
    loginMsg = '';
    try {
      const r = await fetch('/api/collections/users/auth-with-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identity: username, password: pw }),
      });
      const text = await r.text();
      let j: { token?: string; record?: { username?: string; id?: string }; message?: string } = {};
      try { j = text ? JSON.parse(text) : {}; } catch { /* not JSON */ }
      if (!r.ok) {
        if (r.status === 404) {
          loginMsg = lang === 'ru'
            ? 'Авторизация временно недоступна (сервис настраивается).'
            : 'Login temporarily unavailable (service is being set up).';
          return;
        }
        loginMsg = (lang === 'ru' ? 'Не удалось войти: ' : 'Login failed: ') + (j?.message || r.status);
        return;
      }
      localStorage.setItem('divcalc:auth', JSON.stringify({ token: j.token, username: j.record?.username, id: j.record?.id }));
      username = j.record?.username || username;
      loggedIn = true;
      loginMsg = '';
      pw = '';
      setTimeout(() => { authOpen = false; }, 500);
    } catch {
      loginMsg = lang === 'ru'
        ? 'Сервер недоступен. Попробуй позже или напиши через форму обратной связи.'
        : 'Server unavailable. Try later or use the feedback form.';
    } finally {
      busy = false;
    }
  }

  async function doRegister() {
    if (!username || !email || !pw || !pwConfirm) {
      loginMsg = lang === 'ru' ? 'Заполни все поля' : 'Fill all fields';
      return;
    }
    if (pw !== pwConfirm) {
      loginMsg = lang === 'ru' ? 'Пароли не совпадают' : 'Passwords do not match';
      return;
    }
    if (pw.length < 8) {
      loginMsg = lang === 'ru' ? 'Пароль — минимум 8 символов' : 'Password min 8 chars';
      return;
    }
    busy = true;
    loginMsg = '';
    try {
      const r = await fetch('/api/collections/users/records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password: pw, passwordConfirm: pwConfirm }),
      });
      const text = await r.text();
      let j: { data?: Record<string, { message?: string }>; message?: string } = {};
      try { j = text ? JSON.parse(text) : {}; } catch { /* not JSON */ }
      if (!r.ok) {
        if (r.status === 404) {
          loginMsg = lang === 'ru'
            ? 'Регистрация временно недоступна (сервис настраивается). Попробуй позже.'
            : 'Registration temporarily unavailable (service is being set up). Try later.';
          return;
        }
        const fieldErr = j?.data ? Object.entries(j.data).map(([k, v]) => `${k}: ${(v as { message?: string })?.message || ''}`).join('; ') : '';
        loginMsg = (lang === 'ru' ? 'Регистрация не удалась: ' : 'Register failed: ') + (fieldErr || j?.message || r.status);
        return;
      }
      await doLogin();
    } catch {
      loginMsg = lang === 'ru'
        ? 'Сервер недоступен. Попробуй позже или напиши через форму обратной связи.'
        : 'Server unavailable. Try later or use the feedback form.';
    } finally {
      busy = false;
    }
  }

  async function doGoogleOAuth() {
    busy = true;
    loginMsg = '';
    try {
      const methodsR = await fetch('/api/collections/users/auth-methods');
      const methods = await methodsR.json();
      const google = methods?.authProviders?.find((p: { name: string }) => p.name === 'google');
      if (!google) {
        loginMsg = lang === 'ru'
          ? 'Google OAuth не настроен на сервере.'
          : 'Google OAuth not configured.';
        return;
      }
      const redirectUrl = `${window.location.origin}/oauth-redirect.html`;
      const authUrl = google.authUrl + encodeURIComponent(redirectUrl);
      sessionStorage.setItem('divcalc:oauth_pkce', JSON.stringify({
        codeVerifier: google.codeVerifier,
        state: google.state,
        provider: 'google',
        redirectUrl,
      }));
      const popup = window.open(authUrl, 'google_oauth', 'width=500,height=650');
      if (!popup) {
        loginMsg = lang === 'ru' ? 'Браузер заблокировал popup.' : 'Browser blocked popup.';
        return;
      }
      localStorage.removeItem('divcalc:oauth_response');
      const result = await new Promise<{ code: string; state: string }>((resolve, reject) => {
        let done = false;
        const finish = (payload: { code?: string; state?: string; error?: string }) => {
          if (done) return;
          done = true;
          cleanup();
          if (payload.error) reject(new Error(payload.error));
          else if (payload.code) resolve({ code: payload.code, state: payload.state || '' });
          else reject(new Error('empty response'));
        };
        const onMsg = (e: MessageEvent) => {
          if (e.origin !== window.location.origin) return;
          if (e.data?.type !== 'divcalc:oauth') return;
          finish(e.data);
        };
        const onStorage = (e: StorageEvent) => {
          if (e.key !== 'divcalc:oauth_response' || !e.newValue) return;
          try { finish(JSON.parse(e.newValue)); } catch { /* ignore */ }
        };
        let bc: BroadcastChannel | null = null;
        try {
          bc = new BroadcastChannel('divcalc:oauth');
          bc.onmessage = (e) => finish(e.data || {});
        } catch { /* old browser */ }
        window.addEventListener('message', onMsg);
        window.addEventListener('storage', onStorage);
        const interval = setInterval(() => {
          const v = localStorage.getItem('divcalc:oauth_response');
          if (v) {
            try { finish(JSON.parse(v)); } catch { /* ignore */ }
            return;
          }
          if (popup.closed) finish({ error: 'cancelled' });
        }, 500);
        const cleanup = () => {
          clearInterval(interval);
          window.removeEventListener('message', onMsg);
          window.removeEventListener('storage', onStorage);
          if (bc) bc.close();
          localStorage.removeItem('divcalc:oauth_response');
        };
      });
      const pkce = JSON.parse(sessionStorage.getItem('divcalc:oauth_pkce') || '{}');
      if (result.state !== pkce.state) {
        loginMsg = lang === 'ru' ? 'OAuth state mismatch.' : 'OAuth state mismatch.';
        return;
      }
      const exchangeR = await fetch('/api/collections/users/auth-with-oauth2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'google',
          code: result.code,
          codeVerifier: pkce.codeVerifier,
          redirectUrl: pkce.redirectUrl,
        }),
      });
      const j = await exchangeR.json();
      if (!exchangeR.ok) {
        loginMsg = (lang === 'ru' ? 'Google вход не удался: ' : 'Google sign-in failed: ') + (j?.message || exchangeR.status);
        return;
      }
      localStorage.setItem('divcalc:auth', JSON.stringify({
        token: j.token,
        username: j.record?.username || j.record?.email,
        id: j.record?.id,
      }));
      username = j.record?.username || j.record?.email || '';
      loggedIn = true;
      loginMsg = '';
      sessionStorage.removeItem('divcalc:oauth_pkce');
      setTimeout(() => { authOpen = false; }, 400);
    } catch (e) {
      const msg = String(e);
      if (msg.includes('cancelled')) loginMsg = lang === 'ru' ? 'Отменено.' : 'Cancelled.';
      else loginMsg = 'OAuth: ' + msg;
    } finally {
      busy = false;
    }
  }

  function logout() {
    try { localStorage.removeItem('divcalc:auth'); } catch { /* ignore */ }
    loggedIn = false;
    username = '';
    email = '';
    pw = '';
    pwConfirm = '';
    loginMsg = '';
    authOpen = false;
  }

  async function submitBug() {
    const payload = {
      type: 'header-quick',
      title: bugTitle || 'Bug report from user',
      description: bugBody || '',
      page_url: location.href,
      user_agent: navigator.userAgent,
      lang,
      user: username || 'anon',
      _subject: 'Division 2 Calc — quick bug from header',
    };
    try {
      await fetch('https://formspree.io/f/mlgadraa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch { /* ignore — fire-and-forget */ }
    bugOpen = false;
    bugTitle = '';
    bugBody = '';
  }

  function closeOverlays() {
    authOpen = false;
    bugOpen = false;
    buildsOpen = false;
  }

  let shareStatus = $state<'' | 'copied'>('');
  async function shareBuild() {
    if (!build) return;
    const q = encodeBuild(build);
    const url = `${location.origin}${location.pathname}?${q}`;
    try {
      await navigator.clipboard.writeText(url);
      shareStatus = 'copied';
      setTimeout(() => (shareStatus = ''), 1500);
    } catch {
      history.replaceState({}, '', `?${q}`);
    }
  }
  function shareTg() {
    if (!build) return;
    const q = encodeBuild(build);
    const url = `${location.origin}${location.pathname}?${q}`;
    const weapon = build.weaponId && data ? data.byId.weapon.get(build.weaponId) : null;
    const nm = weapon ? (i18next.t(weapon.id, { ns: 'weapons', defaultValue: weapon.id }) as string) : '';
    shareToTelegram(url, nm);
  }
  async function exportPng() {
    const weapon = build?.weaponId && data ? data.byId.weapon.get(build.weaponId) : null;
    const nm = weapon ? (i18next.t(weapon.id, { ns: 'weapons', defaultValue: weapon.id }) as string) : 'build';
    try {
      await exportBuildAsPng(nm);
    } catch (e) {
      alert((lang === 'ru' ? 'PNG экспорт не удался: ' : 'PNG export failed: ') + String(e));
    }
  }
  // Silence unused warnings when props not provided
  void summary; void refreshList;
</script>

<div class="ha-row">
  {#if loggedIn}
    <button
      class="ha-btn on"
      onclick={() => openAuth('login')}
      title={lang === 'ru' ? 'Профиль' : 'Profile'}
    >
      👤 {username}
    </button>
  {:else}
    <button class="ha-btn" onclick={() => openAuth('login')}>
      👤 {lang === 'ru' ? 'Войти' : 'Sign in'}
    </button>
    <button class="ha-btn reg" onclick={() => openAuth('register')}>
      ✨ {lang === 'ru' ? 'Регистрация' : 'Register'}
    </button>
  {/if}
  {#if build}
    <button
      class="ha-btn share"
      onclick={shareBuild}
      title={lang === 'ru' ? 'Скопировать ссылку на билд' : 'Copy build URL'}
    >
      {shareStatus === 'copied' ? '✓ ' : '🔗 '}{lang === 'ru' ? 'Поделиться' : 'Share'}
    </button>
    <button class="ha-btn" onclick={shareTg} title="Telegram">✈</button>
    <button class="ha-btn" onclick={exportPng} title="Export PNG">📷</button>
    <button
      class="ha-btn builds"
      onclick={() => { buildsOpen = !buildsOpen; authOpen = false; bugOpen = false; }}
      title={lang === 'ru' ? 'Сохранённые билды' : 'Saved builds'}
    >
      💾 {lang === 'ru' ? 'Билды' : 'Builds'}{#if savedList.length > 0}<span class="count">{savedList.length}</span>{/if}
    </button>
  {/if}
  <button
    class="ha-btn bug"
    onclick={() => { bugOpen = !bugOpen; authOpen = false; buildsOpen = false; }}
    title={lang === 'ru' ? 'Сообщить о баге' : 'Bug report'}
  >
    🐛
  </button>
</div>

{#if buildsOpen && build}
  <div class="ha-overlay" onclick={closeOverlays} role="presentation"></div>
  <div class="ha-pop wide">
    <div class="ha-pop-title">💾 {lang === 'ru' ? 'Сохранённые билды' : 'Saved builds'}</div>
    <div class="save-row">
      <input
        class="input"
        type="text"
        placeholder={lang === 'ru' ? 'Имя билда' : 'Build name'}
        bind:value={saveName}
        onkeydown={(e) => { if (e.key === 'Enter') doSave(); }}
      />
      <button class="btn primary" onclick={doSave}>
        {lang === 'ru' ? 'Сохранить' : 'Save'}
      </button>
    </div>
    {#if savedList.length > 0}
      <ul class="builds-list">
        {#each savedList as s (s.name)}
          <li>
            <button class="load-btn" onclick={() => doLoad(s)}>
              <span class="l-name">{s.name}</span>
              <span class="l-date">{new Date(s.at).toLocaleDateString()}</span>
            </button>
            <button class="del-btn" onclick={() => doDelete(s.name)} title={lang === 'ru' ? 'Удалить' : 'Delete'}>×</button>
          </li>
        {/each}
      </ul>
    {:else}
      <div class="ha-note">{lang === 'ru' ? 'Пока нет сохранённых билдов' : 'No saved builds yet'}</div>
    {/if}
  </div>
{/if}

{#if authOpen}
  <div class="ha-overlay" onclick={closeOverlays} role="presentation"></div>
  <div class="ha-pop">
    <div class="ha-pop-title">
      {#if loggedIn}
        {lang === 'ru' ? 'Аккаунт' : 'Account'}
      {:else if authMode === 'login'}
        {lang === 'ru' ? 'Вход' : 'Sign in'}
      {:else}
        {lang === 'ru' ? 'Создание аккаунта' : 'Create account'}
      {/if}
    </div>
    {#if !loggedIn}
      <div class="ha-oauth-row">
        <button
          class="ha-oauth-icon"
          disabled={busy}
          onclick={doGoogleOAuth}
          type="button"
          aria-label="Google"
          title={lang === 'ru'
            ? (authMode === 'register' ? 'Регистрация через Google' : 'Войти через Google')
            : (authMode === 'register' ? 'Sign up with Google' : 'Sign in with Google')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.71-1.57 2.7-3.89 2.7-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.97 7.3C4.68 5.16 6.66 3.58 9 3.58z"/>
          </svg>
        </button>
        <span class="ha-oauth-hint">{lang === 'ru' ? 'быстрый вход через Google' : 'quick sign-in via Google'}</span>
      </div>
      <div class="ha-tabs">
        <button class="ha-tab" class:active={authMode === 'login'} onclick={() => (authMode = 'login')}>
          {lang === 'ru' ? '👤 Вход' : '👤 Sign in'}
        </button>
        <button class="ha-tab" class:active={authMode === 'register'} onclick={() => (authMode = 'register')}>
          {lang === 'ru' ? '✨ Регистрация' : '✨ Register'}
        </button>
      </div>
      <label>
        <span>{authMode === 'login' ? (lang === 'ru' ? 'Имя или email' : 'Username or email') : (lang === 'ru' ? 'Имя пользователя' : 'Username')}</span>
        <input class="input" type="text" bind:value={username} autocomplete="username" />
      </label>
      {#if authMode === 'register'}
        <label>
          <span>Email</span>
          <input class="input" type="email" bind:value={email} autocomplete="email" />
        </label>
      {/if}
      <label>
        <span>{lang === 'ru' ? 'Пароль' : 'Password'}</span>
        <input class="input" type="password" bind:value={pw} autocomplete={authMode === 'login' ? 'current-password' : 'new-password'} />
      </label>
      {#if authMode === 'register'}
        <label>
          <span>{lang === 'ru' ? 'Повтор пароля' : 'Confirm password'}</span>
          <input class="input" type="password" bind:value={pwConfirm} autocomplete="new-password" />
        </label>
      {/if}
      <button class="btn primary" disabled={busy} onclick={authMode === 'login' ? doLogin : doRegister}>
        {#if busy}…{:else if authMode === 'login'}{lang === 'ru' ? 'Войти' : 'Sign in'}{:else}{lang === 'ru' ? 'Создать аккаунт' : 'Create account'}{/if}
      </button>
      {#if loginMsg}<div class="ha-msg err">{loginMsg}</div>{/if}
      <div class="ha-note">🔒 {lang === 'ru' ? 'Данные на нашем сервере, без сторонних' : 'Data on our server, no 3rd parties'}</div>
    {:else}
      <div class="ha-row-wide"><span>{lang === 'ru' ? 'Вы вошли как' : 'Logged in as'}:</span> <b>{username}</b></div>
      <button class="btn danger" onclick={logout}>{lang === 'ru' ? 'Выйти' : 'Log out'}</button>
    {/if}
  </div>
{/if}

{#if bugOpen}
  <div class="ha-overlay" onclick={closeOverlays} role="presentation"></div>
  <div class="ha-pop">
    <div class="ha-pop-title">{lang === 'ru' ? 'Сообщить о баге' : 'Report a bug'}</div>
    <label>
      <span>{lang === 'ru' ? 'Заголовок' : 'Title'}</span>
      <input
        class="input"
        type="text"
        bind:value={bugTitle}
        placeholder={lang === 'ru' ? 'Краткое описание' : 'Short summary'}
      />
    </label>
    <label>
      <span>{lang === 'ru' ? 'Что произошло?' : 'What happened?'}</span>
      <textarea
        class="input"
        rows="5"
        bind:value={bugBody}
        placeholder={lang === 'ru' ? 'Шаги, что ожидал, что получилось...' : 'Steps to reproduce, expected vs actual...'}
      ></textarea>
    </label>
    <button class="btn primary" onclick={submitBug}>
      ↗ {lang === 'ru' ? 'Открыть в GitHub' : 'Open in GitHub'}
    </button>
    <div class="ha-note">{lang === 'ru' ? 'Нужен GitHub-аккаунт' : 'Needs a GitHub account'}</div>
  </div>
{/if}

<style>
  .ha-row { display: flex; gap: 6px; }
  .ha-btn {
    padding: 5px 10px;
    background: var(--bg-2); border: 1px solid var(--border);
    color: var(--muted);
    border-radius: 999px;
    cursor: pointer;
    font: 700 10px/1 var(--f-display); letter-spacing: .1em;
    text-transform: uppercase;
    transition: all .12s;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .ha-btn:hover { border-color: var(--border-hi); color: var(--text); }
  .ha-btn.on {
    color: var(--orange); border-color: rgba(254,175,16,.5);
    background: rgba(254,175,16,.08);
  }
  .ha-btn.reg {
    color: var(--set); border-color: rgba(61,166,120,.4);
    background: rgba(61,166,120,.08);
  }
  .ha-btn.reg:hover {
    background: rgba(61,166,120,.18);
    border-color: var(--set);
  }
  .ha-btn.bug:hover { color: var(--red); border-color: rgba(239,83,80,.5); }
  .ha-btn.builds {
    color: var(--brand); border-color: rgba(216,221,232,.4);
    background: rgba(216,221,232,.06);
  }
  .ha-btn.builds:hover { background: rgba(216,221,232,.15); border-color: var(--brand); }
  .ha-btn.share {
    color: var(--orange); border-color: rgba(254,175,16,.5);
    background: rgba(254,175,16,.08);
  }
  .ha-btn.share:hover { background: rgba(254,175,16,.18); border-color: var(--orange); }
  .ha-btn .count {
    margin-left: 4px; padding: 2px 6px;
    background: var(--orange); color: #000;
    border-radius: 999px;
    font: 700 9px/1 var(--f-display);
    letter-spacing: 0;
  }
  .ha-pop.wide { width: 360px; max-height: 70vh; overflow-y: auto; }
  .save-row { display: grid; grid-template-columns: 1fr 100px; gap: 6px; }
  .save-row .input { padding: 6px 10px; font-size: 12px; }
  .builds-list { list-style: none; margin: 6px 0 0; padding: 0; display: flex; flex-direction: column; gap: 3px; }
  .builds-list li { display: flex; gap: 3px; }
  .load-btn {
    flex: 1; display: flex; justify-content: space-between; align-items: center;
    padding: 6px 10px;
    background: var(--bg-2); border: 1px solid var(--border);
    color: var(--text); border-radius: var(--r-sm);
    cursor: pointer; font-size: 11px;
  }
  .load-btn:hover { background: rgba(254,175,16,.08); border-color: rgba(254,175,16,.3); color: var(--orange-hi, #ffa500); }
  .l-name { font-weight: 600; }
  .l-date { font-size: 9px; color: var(--muted); font-family: var(--f-mono); }
  .del-btn {
    padding: 0 8px; background: transparent;
    border: 1px solid var(--border); color: var(--red);
    cursor: pointer; border-radius: var(--r-sm);
  }
  .del-btn:hover { background: rgba(255,94,98,.1); border-color: var(--red); }
  .ha-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.3); z-index: 50;
  }
  .ha-pop {
    position: fixed; top: 70px; right: 20px;
    width: 320px; z-index: 51;
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--r); padding: 14px;
    display: flex; flex-direction: column; gap: 8px;
    box-shadow: 0 20px 40px rgba(0,0,0,.6);
  }
  .ha-pop-title {
    font: 700 11px/1 var(--f-display); letter-spacing: .14em;
    color: var(--orange); text-transform: uppercase;
    padding-bottom: 6px; border-bottom: 1px solid var(--border);
  }
  .ha-oauth-row {
    display: flex; align-items: center; gap: 10px;
    padding: 4px 0 8px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 4px;
  }
  .ha-oauth-icon {
    flex: 0 0 auto;
    width: 38px; height: 38px;
    display: flex; align-items: center; justify-content: center;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: 50%;
    cursor: pointer;
    transition: border-color .15s, background .15s, transform .15s;
  }
  .ha-oauth-icon:hover { border-color: var(--orange); background: var(--card-2); transform: translateY(-1px); }
  .ha-oauth-icon:disabled { opacity: .5; cursor: not-allowed; }
  .ha-oauth-hint {
    font-size: 11px; color: var(--muted);
    letter-spacing: .04em;
  }
  .ha-tabs { display: flex; gap: 4px; }
  .ha-tab {
    flex: 1; padding: 7px 10px;
    background: var(--bg-2); border: 1px solid var(--border);
    color: var(--muted);
    border-radius: var(--r);
    cursor: pointer;
    font: 700 9px/1 var(--f-display); letter-spacing: .1em;
    text-transform: uppercase;
    transition: all .12s;
  }
  .ha-tab:hover { border-color: var(--border-hi); color: var(--text-dim); }
  .ha-tab.active {
    background: var(--orange); color: #000; border-color: var(--orange);
  }
  .ha-pop label { display: flex; flex-direction: column; gap: 3px; }
  .ha-pop label span { font: 700 9px/1 var(--f-display); color: var(--muted); letter-spacing: .1em; text-transform: uppercase; }
  .ha-pop .input { padding: 6px 8px; font-size: 12px; }
  .ha-pop textarea.input { resize: vertical; min-height: 90px; font-family: var(--f-body); line-height: 1.4; }
  .ha-msg { font-size: 11px; color: var(--green); white-space: pre-wrap; word-break: break-word; }
  .ha-msg.err { color: #ff8080; }
  .ha-note { font-size: 10px; color: var(--muted); font-style: italic; }
  .ha-row-wide { font-size: 12px; color: var(--text-dim); }
  .ha-row-wide b { color: var(--text); }
  .btn.primary { background: var(--orange); border-color: var(--orange); color: #000; }
  .btn.primary:hover { background: #ffa726; }
  .btn.primary:disabled { opacity: .5; cursor: not-allowed; }
  .btn.danger { color: var(--red); border-color: rgba(239,83,80,.5); }

  @media (max-width: 700px) {
    .ha-pop { left: 10px; right: 10px; width: auto; top: 60px; }
    .ha-btn { padding: 4px 8px; font-size: 9px; }
  }
</style>
